import { z } from 'zod';
import type { IQuickStartModel } from '@/types/screening/quick-start';
import { requireFields, yesNo } from '@utils/form-schema';

// Mirrors Nkr.Utilities ValidDonationAgeAttribute, which the React flow bypasses:
// the api/register/quick-start endpoint takes BirthDate without revalidating it.
const yearsAgo = (years: number) => {
    const date = new Date();

    date.setHours(0, 0, 0, 0);
    date.setFullYear(date.getFullYear() - years);

    return date;
};

export const createQuickStartSchema = (
    model: Pick<IQuickStartModel, 'needsName' | 'showOfferToConnectToDonor' | 'isDonorConnectNegC'>,
) => {
    return z
        .object({
            firstName: z.string(),
            lastName: z.string(),
            gender: z.string().catch(''),
            birthDate: z.string().optional(),
            birthMonth: z.string(),
            birthDay: z.string(),
            birthYear: z.string(),
            hasConfirmedBirthDateMatchesId: z.boolean(),
            heightFeet: z.string(),
            heightInches: z.string(),
            weightLbs: z.string(),
            optOutOfDonorConnect: z.boolean(),
            connectToDonorIsDesired: yesNo.catch(''),
        })
        .superRefine((data, ctx) => {
            const requireRange = (field: keyof typeof data, value: string, min: number, max: number, rangeMessage: string) => {
                if (!value) {
                    ctx.addIssue({ code: 'custom', path: [field], message: 'Required' });
                    return;
                }

                const parsed = parseInt(value);

                if (Number.isNaN(parsed) || parsed < min || parsed > max) {
                    ctx.addIssue({ code: 'custom', path: [field], message: rangeMessage });
                }
            };

            if (model.needsName) {
                requireFields(ctx, data, 'firstName', 'lastName');
            }

            requireFields(ctx, data, 'gender', 'birthMonth', 'birthDay', 'birthYear');

            if (data.birthMonth && data.birthDay && data.birthYear) {
                let birthMonth = parseInt(data.birthMonth) < 10 ? '0' + data.birthMonth : data.birthMonth;
                let birthDay = parseInt(data.birthDay) < 10 ? '0' + data.birthDay : data.birthDay;

                const birthDate = new Date(`${data.birthYear}-${birthMonth}-${birthDay}` + 'T00:00:00');

                if (Number.isNaN(birthDate.getTime())) {
                    ctx.addIssue({ code: 'custom', path: ['birthDate'], message: 'Invalid Date' });
                } else if (birthDate > yearsAgo(18)) {
                    ctx.addIssue({ code: 'custom', path: ['birthDate'], message: 'You must be at least 18 to register' });
                } else if (birthDate < yearsAgo(120)) {
                    ctx.addIssue({ code: 'custom', path: ['birthDate'], message: 'You must be under 120 to register' });
                }
            } else {
                ctx.addIssue({ code: 'custom', path: ['birthDate'], message: 'Please select month, day and year' });
            }

            if (!data.hasConfirmedBirthDateMatchesId) {
                ctx.addIssue({ code: 'custom', path: ['hasConfirmedBirthDateMatchesId'], message: 'Required' });
            }

            requireRange('heightFeet', data.heightFeet, 4, 7, 'Required');
            requireRange('heightInches', data.heightInches, 0, 11, 'Out of Range');
            requireRange('weightLbs', data.weightLbs, 50, 400, 'Out of Range');

            // Donor-mentor opt-in answer (only when the mentor card is shown).
            if (model.showOfferToConnectToDonor && !model.isDonorConnectNegC) {
                requireFields(ctx, data, 'connectToDonorIsDesired');
            }
        });
};
